import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import { Event } from '@process-advisor/process-miner/api/events';
import { useApiListener } from '@process-advisor/process-miner/shared/hooks/useApiListener';
import { getColor, getHeight, getShadow, getZIndex, type Theme } from '@process-advisor/process-miner/shared/theme';
import { type ContextMenuOption } from '@process-advisor/process-miner/api/methods/shared/contextMenu';
import { Options } from '@process-advisor/process-miner/components/contextMenu/options';
import { useClickOutside } from '@process-advisor/process-miner/shared/hooks/useClickOutside';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';

const Wrapper = styled.div<{ y: number, x: number, isAbove: boolean }>`
    display: flex;
    flex-direction: column;

    background-color: ${getColor('white')};
    box-shadow: ${getShadow('hover')};
    border-radius: 4px;
    padding: 8px 0;

    position: absolute;
    top: ${({ y, isAbove, ...props }) =>
        y + (getHeight('contextMenuOption')(props) / 2 * (isAbove ? -1 : 1))}px;
    left: ${({ x }) => x}px;
    z-index: ${getZIndex('contextMenu')};
    transform: translate(-50%, ${({ isAbove }) => isAbove ? '-100%' : 0});
`;

export const ContextMenu = () => {
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [show, setShow] = React.useState(false);
    const [coords, setCoords] = React.useState<{ x: number, y: number}>({ x: 0, y: 0 });
    const [x, setX] = React.useState(0);
    const [y, setY] = React.useState(0);
    const [options, setOptions] = React.useState<ContextMenuOption[]>([]);
    const [isAbove, setIsAbove] = React.useState(false);
    const theme = useTheme() as Theme;
    
    const showContextMenu = ({ x, y, options }) => {
        setShow(true);
        setCoords({ x, y });
        setOptions(options);
    };
    const hideContextMenu = () => {
        const diagram = apiProvider.getCurrentDiagram();
        diagram.currentTool.stopTool();
        setShow(false);
        diagram.toolManager.hideToolTip();
    };

    useApiListener(Event.showContextMenu, showContextMenu);
    useClickOutside(wrapperRef, hideContextMenu);

    React.useEffect(() => {
        wrapperRef.current?.addEventListener('contextmenu', (e) => e.preventDefault());
    }, [show, wrapperRef]);

    React.useEffect(() => {
        if (show) {
            const height = theme.height.contextMenuOption * options.length;
            const diagramHeight = apiProvider.getCurrentDiagram().div.getBoundingClientRect().height;
            setIsAbove(coords.y > (diagramHeight / 2) && coords.y + height > diagramHeight);
        }
    }, [wrapperRef, show, coords, options, theme]);

    React.useEffect(() => {
        if (show) {
            const { top, left } = apiProvider.getCurrentDiagram().div.getBoundingClientRect();
            setX(coords.x + left);
            setY(coords.y + top);
        }
    }, [show, coords]);

    if (!show) {
        return null;
    }
    return (
        <Wrapper ref={wrapperRef} isAbove={isAbove} x={x} y={y}>
            <Options
                hideContextMenu={hideContextMenu}
                customKey='root'
                contextMenuOptions={options} />
        </Wrapper>
    );
};

